import {
  type BookLanguages,
  callsForRun,
  ENGLISH,
  markersByNote,
  numberNotes,
  placementsOf,
  runOf,
} from '@app/studio'
import { useQuery } from '@tanstack/react-query'
import { ALargeSmall, BookOpen, Printer, TableOfContents } from 'lucide-react'
import { type ReactNode, useCallback, useMemo, useState } from 'react'
import { sharedBookPdfUrl } from '../../lib/pdf'
import { useTRPC } from '../../lib/trpc'
import { Toggle } from '../../ui'
import {
  Companion,
  type CompanionAction,
  CompanionOpener,
  CompanionRail,
  type CompanionView,
} from '../../ui/Companion'
import { Contents } from '../../ui/Contents'
import { Modal } from '../../ui/Modal'
import { useDebounced } from '../../ui/useDebounced'
import { BookPreviewSheet } from './BookPreviewSheet'
import { BookProvider, BookReader, InlineRun } from './BookReader'
import { contentsEntries } from './contents-entries'
import { glideToElement } from './go-to'
import { PublicReaderHeader, PublicReaderMark } from './PublicReaderHeader'
import { ReaderDisplay } from './ReaderDisplay'
import { useReaderSettings, useReadingPaper } from './reader-settings'
import { useWhereIAm } from './useFollowing'
import { useLiveBook } from './useLiveBook'
import { useLiveText } from './useLiveText'
import './shared-book.css'

/**
 * A book, read from a public link.
 *
 * Outside auth entirely: nobody signs in, and the token in the URL is the whole
 * authorization. So this page wears no org chrome — no masthead, no nav, no
 * congregation. A book belongs to a person, and putting a shul's furniture
 * around it would claim the shul published it.
 *
 * What is here is the book and the way to read it, which is also what makes the
 * page honest about what a public link is: a reading copy, not an account.
 */
export function SharedBookPage({ token }: { token: string }) {
  const trpc = useTRPC()
  const book = useQuery({
    ...trpc.share.read.queryOptions({ token }),
    /* A link that does not open is an ANSWER, not a fault. Retried, it leaves
       the reader on an empty page for several seconds before saying anything —
       and asks the server the same settled question three times. */
    retry: false,
  })

  const sections = useMemo(() => book.data?.sections ?? [], [book.data])

  /*
   * Nobody is signed in here, so the token is what opens the room, exactly as
   * it is what opens the book — and a reader on a link is counted rather than
   * named, so there is nobody here for anybody to follow. They still say where
   * they are: what makes them uncountable is having no identity, not having no
   * place in the book.
   */
  /* Every passage drawn, which is what says the book has arrived. */
  const blockCount = useMemo(
    () =>
      sections.reduce(function walk(n, p): number {
        return n + p.blocks.length + p.children.reduce(walk, 0)
      }, 0),
    [sections],
  )
  const iAm = useWhereIAm(blockCount)
  /* Present in the book, so whoever has it open sees a reader here. This copy
     shows nobody back: a guest has no way to follow or lead. */
  useLiveBook({ token }, iAm)

  /* A reading copy is a reading of the book, so it goes stale exactly when a
     signed-in one does. Nothing here is writable, so no draft can be held. */
  useLiveText({ token, openBlockId: null, dataUpdatedAt: book.dataUpdatedAt })

  /** The book's own languages — a reading copy binds the way the book does. */
  const languages = useMemo<BookLanguages>(
    () =>
      book.data
        ? {
            primary: book.data.primaryLanguage,
            secondary: book.data.secondaryLanguage,
          }
        : ENGLISH,
    [book.data],
  )

  /* Computed here from what arrived, the same walk the printed book uses — so a
     marker in the text and the note it opens cannot disagree. The policy comes
     with the book for the same reason: a published copy numbering by the
     default would number differently from the book it was published from. */
  const numbered = useMemo(
    () =>
      numberNotes({
        sections,
        policy: book.data?.notePolicy,
        placements: placementsOf(book.data?.notes ?? []),
      }),
    [sections, book.data],
  )
  const markers = useMemo(() => markersByNote(numbered), [numbered])

  /* The same walk the reader's own contents makes, and the same one the printed
     copy sets: a public reading copy lists the parts the book has. */
  const contents = useMemo(() => contentsEntries(sections), [sections])
  const [listing, setListing] = useState(false)
  /* Revealed above the reading, so what it changes stays in view while it is
     open. This page has no shell, so the strip is the only chrome it has. */
  const [displaying, setDisplaying] = useState(false)
  const [printing, setPrinting] = useState(false)
  const [previewing, setPreviewing] = useState(false)
  const [includeCover, setIncludeCover] = useState(true)
  const printBook = useCallback(() => {
    setPreviewing(false)
    if (book.data?.hasCover === false)
      window.open(sharedBookPdfUrl(token), '_blank')
    else setPrinting(true)
  }, [token, book.data?.hasCover])
  const { settings, update } = useReaderSettings()
  useReadingPaper(settings.tint)
  const [typed, setTyped] = useState('')
  const query = useDebounced(typed, 150)

  /*
   * Nothing is fixed over this page, so a part arrives at the top of the window
   * with a hand's breadth above it rather than under a bar that is not there.
   * The band stays open behind the arrival: a contents is how a reader moves
   * through a long book, and closing it after one jump would ask them to open
   * it again for the next.
   */
  const goToPart = useCallback((id: string) => {
    glideToElement(
      document.querySelector(`[data-passage="${CSS.escape(id)}"]`),
      PART_CLEARANCE,
    )
  }, [])

  /*
   * What is beside the book, which here is one thing: its contents.
   *
   * The same band the signed-in reader gets, off the same component and the
   * same view declaration — reading continues underneath it, which is the whole
   * test of a companion. Offered only where the book has named parts, because
   * an entry point onto an empty list teaches a reader to ignore it.
   */
  const views = useMemo<CompanionView[]>(
    () =>
      contents.length
        ? [
            {
              id: CONTENTS_VIEW,
              label: 'Contents',
              icon: <TableOfContents size={15} />,
              search: {
                value: typed,
                onChange: setTyped,
                placeholder: 'Search contents…',
                ariaLabel: 'Search contents',
              },
              render: () => (
                <Contents entries={contents} onGo={goToPart} query={query} />
              ),
            },
          ]
        : [],
    [contents, goToPart, typed, query],
  )

  /* A copy to keep, and the only thing this page DOES. Its own tab: a book is a
     long build, and the reader keeps the page they were on while it runs. */
  const actions = useMemo<CompanionAction[]>(
    () => [
      {
        id: 'display',
        label: 'Display',
        icon: <ALargeSmall size={15} />,
        onSelect: () => setDisplaying((d) => !d),
      },
      {
        id: 'preview',
        label: 'Preview book',
        icon: <BookOpen size={15} />,
        onSelect: () => setPreviewing(true),
      },
      {
        id: 'print',
        label: 'Print',
        icon: <Printer size={15} />,
        /* The cover is offered until the book is known to have none, so a
           press before it has loaded still asks. */
        onSelect: printBook,
      },
    ],
    [printBook],
  )

  const bodies = useMemo(() => {
    const map = new Map<string, ReactNode>()
    for (const n of book.data?.notes ?? []) {
      map.set(
        n.id,
        /* Keyed by the block's own id, which it has — a note's body is
           positional, but its blocks are still rows with identities. It carries
           the book with it because it is drawn outside the reader, where there
           is no provider above it. */
        <BookProvider key={n.id} value={languages}>
          {/* The book's scope, because a note is the book's own text and is
              drawn outside the reader — the faces and the marks come from the
              class rather than from where the panel landed. */}
          <div className="book">
            {n.blocks.map((b) => (
              <p key={b.id}>
                <InlineRun
                  run={runOf(b.content)}
                  calls={callsForRun(
                    b.content,
                    b.calls ?? [],
                    runOf(b.content),
                  )}
                />
              </p>
            ))}
          </div>
        </BookProvider>,
      )
    }
    return map
  }, [book.data, languages])

  if (book.isPending) {
    return (
      <div className="sharedbook" aria-busy="true">
        <PublicReaderHeader />
      </div>
    )
  }

  if (book.error) {
    return (
      <div className="sharedbook">
        <PublicReaderHeader />
        <div className="sharedbook__gone">
          <h1>This link does not open anything.</h1>
          <p>
            It may have been withdrawn by whoever shared it, or copied
            incompletely.
          </p>
        </div>
      </div>
    )
  }

  /* The reading has the window: no bar across it, only the product's mark in
     its corner and the band's rail at the other edge, as a document in Studio
     stands between its rails. */
  return (
    <div className="sharedbook sharedbook--bare">
      {previewing && book.data ? (
        <BookPreviewSheet
          url={sharedBookPdfUrl(token, book.data.hasCover)}
          trim={book.data.trim}
          onClose={() => setPreviewing(false)}
          onPrint={printBook}
        />
      ) : null}
      {printing ? (
        <Modal
          title="Print book"
          onClose={() => setPrinting(false)}
          primary={{
            label: 'Open PDF',
            onSelect: () => {
              window.open(sharedBookPdfUrl(token, includeCover), '_blank')
              setPrinting(false)
            },
          }}
        >
          <Toggle
            checked={includeCover}
            onChange={setIncludeCover}
            label="Include cover"
          />
        </Modal>
      ) : null}
      <PublicReaderMark />
      <div className="sharedbook__body">
        {/* Ahead of the reading: below the roomy edge this is the row at the top of
          the column, and a sticky box written after the book sticks from below
          the book. There is no shell here to portal into at any width, so this
          page carries the row itself. */}
        {/* Down while the display row is open, for the same reason the rail is:
          the opener carries the very action that opened it, so leaving it would
          be two controls closing one thing — and the one up here gives no sign
          that what it opens is already showing. */}
        {!listing && !displaying && (
          <CompanionOpener
            views={views}
            actions={actions}
            onOpen={() => setListing(true)}
          />
        )}
        <div className="sharedbook__main">
          <div className="sharedbook__band">
            <ReaderDisplay
              open={displaying}
              settings={settings}
              onChange={update}
              onClose={() => setDisplaying(false)}
            />
            <BookReader
              title={book.data?.title ?? ''}
              subtitle={book.data?.subtitle}
              sections={sections}
              book={languages}
              typeface={book.data?.face}
              bodies={bodies}
              markers={markers}
            />
          </div>
        </div>

        {/*
         * One strip, wherever the strip currently is: the rail while the contents
         * is shut, the band's own head while it is open. There is no top bar here
         * to hold a page action, so Print rides with it — standing on its own in
         * the reading column it would be a second piece of chrome for a page
         * whose whole design is not to have any, and left on the rail behind an
         * open band it would be a box hanging off the side of one.
         */}
        {listing ? (
          <Companion
            views={views}
            actions={actions}
            active={CONTENTS_VIEW}
            onActiveChange={() => {}}
            onClose={() => setListing(false)}
          />
        ) : displaying ? null : (
          <CompanionRail
            views={views}
            actions={actions}
            onOpen={() => setListing(true)}
          />
        )}
      </div>
    </div>
  )
}

/** The contents view's id — the one thing beside a public reading copy. */
const CONTENTS_VIEW = 'contents'

/**
 * How far below the top of the window a part comes to rest.
 *
 * A number rather than `barClearance()`, which measures the app's fixed bar:
 * this page has none, so that distance would leave a hand's breadth of nothing
 * where the chrome would have been. It is the reading column's own top padding,
 * so a part arrives where the title sits.
 */
const PART_CLEARANCE = 24

/** The inline run a block carries, whichever kind it is. */
